import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, Save, CheckCircle2, ArrowLeft, Download, Send, Video, Trash2, Mail, MessageSquare } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vee-trading-hub/artifacts/k4o9jcwu_VEE%20CONSULTANCY%20LOGO.png";

// Convert ISO/Date to local datetime-local input value (yyyy-MM-ddTHH:mm)
const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Convert local datetime-local to ISO with IST (+05:30) offset
const toISTISO = (localValue) => {
  if (!localValue) return "";
  return `${localValue}:00+05:30`;
};

// Convert ISO YYYY-MM-DD → DD-MM-YYYY (Indian display). Passes through if not ISO.
const fmtDate = (iso) => {
  if (!iso || typeof iso !== "string") return iso || "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso;
};

export default function AdminPage() {
  const [token, setToken] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [webinarAt, setWebinarAt] = useState("");
  const [duration, setDuration] = useState(75);
  const [zoomLink, setZoomLink] = useState("");
  const [liveZoomLink, setLiveZoomLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sendingZoom, setSendingZoom] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [regCount, setRegCount] = useState(null);
  const [sendingReminder, setSendingReminder] = useState("");
  // Permanent Exclude List state
  const [excludeData, setExcludeData] = useState(null);
  const [excludeLoading, setExcludeLoading] = useState(false);
  const [newExcludePhone, setNewExcludePhone] = useState("");
  const [newExcludeEmail, setNewExcludeEmail] = useState("");
  const [newExcludeNote, setNewExcludeNote] = useState("");
  const [addingExclude, setAddingExclude] = useState(false);
  const [removingKey, setRemovingKey] = useState("");

  // Invoice Generator state
  const [invName, setInvName] = useState("");
  const [invMobile, setInvMobile] = useState("");
  const [invState, setInvState] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invGstin, setInvGstin] = useState("");
  const [invCustomNo, setInvCustomNo] = useState("");
  const [invCustomDate, setInvCustomDate] = useState("");
  const [invServiceType, setInvServiceType] = useState("course");
  const [invAmount, setInvAmount] = useState("");
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [invNextNo, setInvNextNo] = useState("");
  const [invSendWa, setInvSendWa] = useState(true);
  const [invSendEmail, setInvSendEmail] = useState(true);
  const [invGenerating, setInvGenerating] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [sigUploaded, setSigUploaded] = useState(false);
  const [logoUploaded, setLogoUploaded] = useState(false);

  const loadExcludeList = async () => {
    setExcludeLoading(true);
    try {
      const r = await axios.get(`${API}/admin/exclude-list?admin_token=${encodeURIComponent(token)}`);
      setExcludeData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load exclude list");
    } finally {
      setExcludeLoading(false);
    }
  };

  const loadInvoices = async () => {
    setInvLoading(true);
    try {
      const r = await axios.get(`${API}/admin/invoices?admin_token=${encodeURIComponent(token)}`);
      setInvoices(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load invoices");
    } finally {
      setInvLoading(false);
    }
  };

  const loadNextInvoiceNo = async () => {
    try {
      const r = await axios.get(`${API}/admin/invoice/next-no?admin_token=${encodeURIComponent(token)}`);
      setInvNextNo(r.data?.next_invoice_no || "");
    } catch { /* silent */ }
  };

  const loadAssetsStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/invoice/assets-status?admin_token=${encodeURIComponent(token)}`);
      setSigUploaded(!!r.data?.signature_uploaded);
      setLogoUploaded(!!r.data?.logo_uploaded);
    } catch { /* silent */ }
  };

  const uploadAsset = async (assetType, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/admin/invoice/upload-asset`, {
          admin_token: token,
          asset_type: assetType,
          file_b64: reader.result,
        });
        toast.success(`✅ ${assetType === "signature" ? "Signature" : "Logo"} uploaded!`);
        loadAssetsStatus();
      } catch (err) {
        toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
      }
    };
    reader.readAsDataURL(file);
  };

  const deleteAsset = async (assetType) => {
    if (!window.confirm(`Delete ${assetType}?`)) return;
    try {
      await axios.post(`${API}/admin/invoice/delete-asset`, {
        admin_token: token,
        asset_type: assetType,
      });
      toast.success(`${assetType} deleted`);
      loadAssetsStatus();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const addToExcludeList = async () => {
    const p = newExcludePhone.trim();
    const e = newExcludeEmail.trim();
    if (!p && !e) {
      toast.error("Phone ya email — kuch toh daalo!");
      return;
    }
    setAddingExclude(true);
    try {
      const r = await axios.post(`${API}/admin/exclude-list-add`, {
        admin_token: token,
        phone: p,
        email: e,
        note: newExcludeNote.trim(),
      });
      if (r.data.status === "already_exists") {
        toast.info("Yeh number/email already excluded list mein hai");
      } else {
        toast.success(`✅ Added to exclude list — ab blasts skip honge!`);
      }
      setNewExcludePhone("");
      setNewExcludeEmail("");
      setNewExcludeNote("");
      await loadExcludeList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally {
      setAddingExclude(false);
    }
  };

  const removeFromExcludeList = async (phone, email) => {
    const key = `${phone}|${email}`;
    setRemovingKey(key);
    try {
      await axios.post(`${API}/admin/exclude-list-remove`, {
        admin_token: token,
        phone: phone || "",
        email: email || "",
      });
      toast.success("✅ Removed — woh number/email wapas blasts mein include hoga");
      await loadExcludeList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally {
      setRemovingKey("");
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => {
        setWebinarAt(toDatetimeLocal(r.data.webinar_at));
        setDuration(r.data.duration_min || 75);
        setZoomLink(r.data.zoom_link || "");
        setLiveZoomLink(r.data.live_zoom_link || "");
      })
      .catch(() => {});
  }, []);

  const tryUnlock = async (e) => {
    e?.preventDefault();
    if (!token.trim()) {
      toast.error("Admin password daalo");
      return;
    }
    setUnlocked(true);
    try {
      const r = await axios.get(`${API}/registrations`);
      setRegCount(r.data?.length ?? 0);
    } catch {
      // ignore
    }
    // Load invoice helpers (next no + assets status)
    loadNextInvoiceNo();
    loadAssetsStatus();
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!webinarAt) {
      toast.error("Date/time select karo");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/config`, {
        admin_token: token,
        webinar_at: toISTISO(webinarAt),
        duration_min: Number(duration) || 75,
        zoom_link: zoomLink.trim(),
        live_zoom_link: liveZoomLink.trim(),
      });
      toast.success("Settings updated! Live ho gaya website pe.");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Wrong password. Try again.");
        setUnlocked(false);
      } else {
        toast.error(err?.response?.data?.detail || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await axios.post(
        `${API}/admin/export-csv`,
        { admin_token: token },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vee_leads_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads CSV downloaded!");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Wrong password. Please re-login.");
        setUnlocked(false);
      } else {
        toast.error("CSV export failed. Try again.");
      }
    } finally {
      setExporting(false);
    }
  };

  const sendZoomLinkToAll = async () => {
    if (sendingZoom) return;
    if (!zoomLink.trim()) {
      toast.error("Pehle Zoom link save karo upar.");
      return;
    }
    const confirm = window.confirm(
      `Sabhi registered users ko Zoom link WhatsApp bhejna hai?\n\nTotal: ${regCount ?? "?"} users\n\nYe action immediate hai — cancel nahi ho sakta.`
    );
    if (!confirm) return;

    setSendingZoom(true);
    try {
      const res = await axios.post(`${API}/admin/send-zoom-link`, {
        admin_token: token,
      });
      const { sent, failed, total } = res.data || {};
      toast.success(`Zoom link sent: ${sent}/${total} (failed: ${failed})`);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Wrong password. Please re-login.");
        setUnlocked(false);
      } else {
        toast.error(err?.response?.data?.detail || "Send failed");
      }
    } finally {
      setSendingZoom(false);
    }
  };

  const clearAllRegistrations = async () => {
    if (clearing) return;
    const phrase = window.prompt(
      `⚠️ DANGER: Sab registrations DELETE ho jayengi (total: ${regCount ?? "?"}).\n\n` +
      `Ye action UNDONE nahi ho sakta!\n\n` +
      `Continue karne ke liye type karein: DELETE`
    );
    if (phrase !== "DELETE") {
      if (phrase !== null) toast.error("Confirmation phrase galat. Cancelled.");
      return;
    }
    setClearing(true);
    try {
      const res = await axios.post(`${API}/admin/clear-registrations`, {
        admin_token: token,
        confirm: "DELETE",
      });
      toast.success(`Cleared ${res.data?.deleted_count ?? 0} registrations.`);
      setRegCount(0);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Wrong password. Please re-login.");
        setUnlocked(false);
      } else {
        toast.error(err?.response?.data?.detail || "Clear failed");
      }
    } finally {
      setClearing(false);
    }
  };

  const sendReminderEmail = async (type, label, channel = "all") => {
    if (sendingReminder) return;
    const channelNote = channel === "whatsapp" ? " (WhatsApp only)" : "";
    const confirm = window.confirm(
      `Send "${label}"${channelNote} reminder to all ${regCount ?? "?"} registered users?\n\n` +
      `Ye action immediate hai — blast cancel nahi ho sakta.`
    );
    if (!confirm) return;

    setSendingReminder(type);
    try {
      const res = await axios.post(`${API}/admin/send-reminder`, {
        type,
        admin_token: token,
        channel,
      });
      const { email, whatsapp, total_registered, duplicates_removed, sent_to } = res.data || {};
      const dupNote = duplicates_removed ? ` | ${duplicates_removed} duplicate phones skipped` : "";
      toast.success(
        `${label} sent — ` +
        `Email: ${email?.sent ?? 0}/${sent_to ?? total_registered}, ` +
        `WhatsApp: ${whatsapp?.sent ?? 0}/${sent_to ?? total_registered}` +
        dupNote
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Wrong password. Please re-login.");
        setUnlocked(false);
      } else {
        toast.error(err?.response?.data?.detail || "Reminder send failed");
      }
    } finally {
      setSendingReminder("");
    }
  };

  // ---- LOGIN SCREEN ----
  if (!unlocked) {
    return (
      <div className="min-h-screen bg-[#03060D] text-slate-200 font-body flex items-center justify-center px-4">
        <form
          onSubmit={tryUnlock}
          className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900/60 p-8 space-y-5"
        >
          <div className="flex justify-center mb-3">
            <img src={LOGO_URL} alt="VEE Consultancy" className="h-14 w-auto object-contain" />
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-display text-2xl text-white mb-1">Admin Access</h1>
            <p className="text-sm text-slate-400">Enter your admin password</p>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block">
              Password
            </Label>
            <Input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="••••••••"
              className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white rounded-lg"
              data-testid="admin-password-input"
              autoFocus
            />
          </div>

          <Button
            type="submit"
            data-testid="admin-unlock-btn"
            className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 rounded-lg font-bold"
          >
            Unlock Admin
          </Button>

          <Link to="/" className="block text-center text-xs text-slate-500 hover:text-emerald-400 font-mono uppercase tracking-widest">
            ← Back to Home
          </Link>
        </form>
      </div>
    );
  }

  // ---- DASHBOARD ----
  return (
    <div className="min-h-screen bg-[#03060D] text-slate-200 font-body">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#03060D]/80 border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="VEE Consultancy" className="h-10 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-300 hover:text-emerald-400 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            View Site
          </Link>
        </div>
      </nav>

      <section className="max-w-3xl mx-auto px-4 md:px-8 py-12">
        <div className="mb-8">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-emerald-400 mb-2">
            Admin Dashboard
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white">
            Webinar Settings
          </h1>
          <p className="text-slate-400 mt-2">
            Update webinar date/time, Zoom link, download leads, aur Zoom link bulk-send karein.
          </p>
        </div>

        {regCount !== null && (
          <div className="mb-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5 flex items-center justify-between">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-emerald-400 mb-1">
                Total Registrations
              </div>
              <div className="font-display text-3xl text-white font-bold" data-testid="reg-count">
                {regCount}
              </div>
            </div>
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
        )}

        <form
          onSubmit={save}
          className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 md:p-8 space-y-5"
          data-testid="admin-form"
        >
          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block">
              Webinar Date & Time (IST)
            </Label>
            <Input
              type="datetime-local"
              value={webinarAt}
              onChange={(e) => setWebinarAt(e.target.value)}
              required
              data-testid="webinar-datetime-input"
              className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white rounded-lg"
            />
            <p className="text-xs text-slate-500 mt-2 font-mono">
              Example: 2026-05-04 04:00 PM
            </p>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block">
              Duration (minutes)
            </Label>
            <Input
              type="number"
              min="15"
              max="300"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              required
              data-testid="duration-input"
              className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white rounded-lg"
            />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block flex items-center gap-2">
              <Video className="w-3.5 h-3.5" />
              Zoom / Webinar Link
            </Label>
            <Input
              type="url"
              value={zoomLink}
              onChange={(e) => setZoomLink(e.target.value)}
              placeholder="https://zoom.us/j/XXXXXXXXXX?pwd=..."
              data-testid="zoom-link-input"
              className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white rounded-lg"
            />
            <p className="text-xs text-slate-500 mt-2 font-mono">
              Ye link users ko &quot;Send Zoom Link&quot; button dabane par WhatsApp pe bheja jayega.
            </p>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2 block flex items-center gap-2">
              <Video className="w-3.5 h-3.5" />
              🔴 Live Trading Zoom Link (Recurring Meeting)
            </Label>
            <Input
              type="url"
              value={liveZoomLink}
              onChange={(e) => setLiveZoomLink(e.target.value)}
              placeholder="https://zoom.us/j/XXXXXXXXXX?pwd=... (recurring)"
              data-testid="live-zoom-link-input"
              className="h-12 bg-slate-900/80 border-slate-700 focus:border-emerald-500 text-white rounded-lg"
            />
            <p className="text-xs text-emerald-400/80 mt-2 font-mono">
              🤖 Auto-reply link — jab user &quot;YES, JOIN ME&quot; button dabaye toh yeh link WhatsApp auto-reply me jayega (webinar_joining_link template ka {"{{1}}"} variable).
            </p>
          </div>

          <Button
            type="submit"
            disabled={saving}
            data-testid="save-config-btn"
            className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 rounded-lg font-bold disabled:opacity-60"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </form>

        {/* Actions Section */}
        <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/40 p-6 md:p-8 space-y-4">
          <div>
            <h2 className="font-display text-xl font-bold text-white mb-1">Lead Actions</h2>
            <p className="text-sm text-slate-400">
              Leads export karo ya sabhi registered users ko Zoom link WhatsApp karo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Button
              type="button"
              onClick={exportCSV}
              disabled={exporting}
              data-testid="export-csv-btn"
              className="h-12 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-bold border border-slate-700 disabled:opacity-60"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? "Exporting..." : "Download Leads CSV"}
            </Button>

            <Button
              type="button"
              onClick={sendZoomLinkToAll}
              disabled={sendingZoom || !zoomLink.trim()}
              data-testid="send-zoom-link-btn"
              className="h-12 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-bold disabled:opacity-60"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingZoom ? "Sending..." : "Send Zoom Link to All"}
            </Button>
          </div>

          <p className="text-xs text-slate-500 pt-2 font-mono">
            ⚠️ &quot;Send Zoom Link to All&quot; sabhi registered users ko immediately WhatsApp karega. Confirmation hi milegi.
          </p>
        </div>

        {/* 3-Webinar Frequency Cap Stats */}
        <div className="mt-8 rounded-2xl border border-purple-500/30 bg-purple-500/5 p-6 md:p-8">
          <h2 className="font-display text-xl font-bold text-purple-300 mb-1 flex items-center gap-2">
            🎯 3-Webinar Auto-Skip Logic
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Har user ko max 3 webinars ke msg jayenge. 3 ke baad **auto-skip** (Meta ban se safe rakhta hai).
            <br/><span className="text-purple-300/80 text-xs">Ek webinar date = 1 count (1 Day Before + 3 Hrs + 5 Min sab milake 1 count).</span>
          </p>
          <BlastFrequencyStats token={token} />
        </div>

        {/* Manual Resend Single Lead */}
        <div className="mt-8 rounded-2xl border border-cyan-500/30 bg-cyan-500/5 p-6 md:p-8">
          <h2 className="font-display text-xl font-bold text-cyan-300 mb-1 flex items-center gap-2">
            📩 Manual Resend (Single Lead)
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Client says <em>&quot;email nahi mila&quot;</em>? Team CALL kar rahi ho? Yahaan mobile number ya email daalo → **Welcome Email + WhatsApp** dobara auto-send ho jayega.
            <br/><span className="text-cyan-300/80 text-xs">Personal WhatsApp se link mat bhejo — yahaan se bhejo, ban nahi hoga.</span>
          </p>
          <ManualResendForm token={token} />
        </div>

        {/* Course Portal — Paid Students + Recordings */}
        <div className="mt-8 rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/5 p-6 md:p-8">
          <h2 className="font-display text-xl font-bold text-fuchsia-300 mb-1 flex items-center gap-2">
            🎓 Student Course Portal
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Paid students ka access + class recordings manage karo. Student portal: <a href="/course-login" target="_blank" rel="noreferrer" className="text-fuchsia-300 underline">/course-login</a>
          </p>
          <CoursePortalManager token={token} />
        </div>

        {/* Email + WhatsApp Reminder Blasts */}
        <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 md:p-8">
          <h2 className="font-display text-xl font-bold text-emerald-400 mb-1 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Reminder Blasts (WhatsApp + Email)
          </h2>
          <p className="text-sm text-slate-400 mb-2">
            🤖 <strong className="text-emerald-300">7 ON-DEMAND BUTTONS</strong> — Jab bhi click karo, current webinar date ke according blast jayega.
          </p>
          <p className="text-sm text-slate-400 mb-4">
            📌 Important: Pehle &quot;Webinar Config&quot; mein date sahi set karo, phir buttons reusable hain — har webinar ke liye!
          </p>

          <div className="grid grid-cols-1 gap-3">
            <Button
              type="button"
              onClick={() => sendReminderEmail("saturday", "1 Day Before Reminder")}
              disabled={sendingReminder !== ""}
              data-testid="reminder-day-before-btn"
              className="h-14 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-bold disabled:opacity-60 justify-start px-4 text-left"
            >
              <Mail className="w-4 h-4 mr-3" />
              <div className="flex-1">
                <div className="text-base">
                  {sendingReminder === "saturday"
                    ? "Sending 1-day-before reminder..."
                    : "📲 Send \"1 Day Before\" Reminder"}
                </div>
                <div className="text-xs text-blue-100/80 font-normal mt-0.5">
                  Use 1 din pehle raat 10 PM (registration confirmation template)
                </div>
              </div>
            </Button>

            <Button
              type="button"
              onClick={() => sendReminderEmail("sunday_morning", "3 Hours To Go Reminder")}
              disabled={sendingReminder !== ""}
              data-testid="reminder-3hrs-btn"
              className="h-14 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-lg font-bold disabled:opacity-60 justify-start px-4 text-left"
            >
              <Mail className="w-4 h-4 mr-3" />
              <div className="flex-1">
                <div className="text-base">
                  {sendingReminder === "sunday_morning"
                    ? "Sending 3 hrs to go reminder..."
                    : "📲 Send \"3 Hours To Go\" Reminder"}
                </div>
                <div className="text-xs text-amber-50/80 font-normal mt-0.5">
                  Use webinar wale din 1 PM (Master Candle today reminder)
                </div>
              </div>
            </Button>

            <Button
              type="button"
              onClick={() => sendReminderEmail("sunday_live", "10 Min To Go LIVE Reminder")}
              disabled={sendingReminder !== ""}
              data-testid="reminder-live-btn"
              className="h-14 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-bold disabled:opacity-60 justify-start px-4 text-left"
            >
              <Mail className="w-4 h-4 mr-3" />
              <div className="flex-1">
                <div className="text-base">
                  {sendingReminder === "sunday_live"
                    ? "Sending LIVE reminder..."
                    : "📲 Send \"10 Min To Go LIVE\" Reminder"}
                </div>
                <div className="text-xs text-red-100/80 font-normal mt-0.5">
                  Use webinar se 10 min pehle (final urgency blast)
                </div>
              </div>
            </Button>

            <Button
              type="button"
              onClick={() => sendReminderEmail("live_trading", "Live Trading Webinar Invite")}
              disabled={sendingReminder !== ""}
              data-testid="reminder-live-trading-btn"
              className="h-14 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-lg font-bold disabled:opacity-60 justify-start px-4 text-left"
            >
              <Mail className="w-4 h-4 mr-3" />
              <div className="flex-1">
                <div className="text-base">
                  {sendingReminder === "live_trading"
                    ? "Sending live-trading invite..."
                    : "🚀 Send \"Live Trading Webinar\" Invite (9:20 AM)"}
                </div>
                <div className="text-xs text-emerald-50/80 font-normal mt-0.5">
                  Use 1 din pehle raat — YES/NOT NOW buttons — high engagement + safe quality
                </div>
              </div>
            </Button>

            <Button
              type="button"
              onClick={() => sendReminderEmail("live_confirmation", "Same-Day Live Session Confirmation", "whatsapp")}
              disabled={sendingReminder !== ""}
              data-testid="reminder-live-confirmation-btn"
              className="h-14 bg-gradient-to-r from-pink-500 to-fuchsia-600 hover:from-pink-600 hover:to-fuchsia-700 text-white rounded-lg font-bold disabled:opacity-60 justify-start px-4 text-left"
            >
              <Mail className="w-4 h-4 mr-3" />
              <div className="flex-1">
                <div className="text-base">
                  {sendingReminder === "live_confirmation"
                    ? "Sending live-session confirmation..."
                    : "🌸 Send \"Same-Day Live Session\" Confirmation (WA)"}
                </div>
                <div className="text-xs text-pink-50/80 font-normal mt-0.5">
                  Use aaj subah 8:45–9:00 AM — sabko final Zoom link direct (no YES/NO buttons)
                </div>
              </div>
            </Button>

            <Button
              type="button"
              onClick={() => sendReminderEmail("live_confirmation", "Live Session — EMAIL BLAST (Zoom Link)", "email")}
              disabled={sendingReminder !== ""}
              data-testid="reminder-live-email-blast-btn"
              className="h-14 bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-black rounded-lg font-bold disabled:opacity-60 justify-start px-4 text-left ring-2 ring-yellow-300/60 shadow-lg shadow-yellow-500/20"
            >
              <Mail className="w-4 h-4 mr-3" />
              <div className="flex-1">
                <div className="text-base">
                  {sendingReminder === "live_confirmation"
                    ? "Sending EMAIL blast..."
                    : "📧 EMAIL BLAST — Live Session Zoom Link (ALL Leads)"}
                </div>
                <div className="text-xs text-black/70 font-normal mt-0.5">
                  🚨 WhatsApp throttled? Use ye! Sabko EMAIL se seedha Zoom link — no Meta involvement
                </div>
              </div>
            </Button>

            <Button
              type="button"
              onClick={() => sendReminderEmail("community_invite", "Join WhatsApp Community Invite", "whatsapp")}
              disabled={sendingReminder !== ""}
              data-testid="reminder-community-invite-btn"
              className="h-14 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold disabled:opacity-60 justify-start px-4 text-left shadow-lg shadow-cyan-500/20"
            >
              <MessageSquare className="w-4 h-4 mr-3" />
              <div className="flex-1">
                <div className="text-base">
                  {sendingReminder === "community_invite"
                    ? "Sending Community Invite..."
                    : "💬 Send \"Join WhatsApp Community\" Invite (WA)"}
                </div>
                <div className="text-xs text-cyan-100/80 font-normal mt-0.5">
                  Sabhi leads ko direct official WhatsApp Community join karne ka invite blast bhejein
                </div>
              </div>
            </Button>
          </div>

          <p className="text-xs text-emerald-400/70 pt-3 font-mono">
            ✅ 100% Reusable — Har naye webinar ke liye sirf date change karo, buttons same hi rahenge!
          </p>
        </div>

        {/* 🚀 Live Trading Blast Response Stats */}
        <BlastResponseStats token={token} />

        {/* Permanent Exclude List — phones/emails that NEVER get blasts */}
        <div className="mt-8 rounded-2xl border border-purple-500/30 bg-purple-500/5 p-6 md:p-8">
          <h2 className="font-display text-xl font-bold text-purple-300 mb-1 flex items-center gap-2">
            <Lock className="w-5 h-5" />
            🚫 Permanent Exclude List
          </h2>
          <p className="text-sm text-slate-400 mb-2">
            Paid customers ya &quot;not interested&quot; logon ke <strong className="text-purple-200">phone/email yahaan add karo — bas 1 baar</strong>.
          </p>
          <p className="text-sm text-slate-400 mb-4">
            ✅ Future ke <strong>SAB blasts</strong> mein yeh numbers/emails <strong>automatically SKIP</strong> honge.
            Wapas include karna ho toh &quot;Remove&quot; click karo.
          </p>

          {/* Add new form */}
          <div className="rounded-xl border border-purple-500/40 bg-slate-900/40 p-4 mb-4">
            <p className="text-sm font-bold text-purple-200 mb-3">➕ Add Phone/Email to Exclude:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs text-slate-300">Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="e.g. 8160816550"
                  value={newExcludePhone}
                  onChange={(e) => setNewExcludePhone(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                  data-testid="exclude-phone-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-300">Email (optional)</Label>
                <Input
                  type="email"
                  placeholder="e.g. paid@gmail.com"
                  value={newExcludeEmail}
                  onChange={(e) => setNewExcludeEmail(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                  data-testid="exclude-email-input"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-300">Note (optional)</Label>
                <Input
                  type="text"
                  placeholder="e.g. Paid customer / Not interested"
                  value={newExcludeNote}
                  onChange={(e) => setNewExcludeNote(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                  data-testid="exclude-note-input"
                />
              </div>
            </div>
            <Button
              type="button"
              onClick={addToExcludeList}
              disabled={addingExclude}
              data-testid="add-exclude-btn"
              className="h-10 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold disabled:opacity-60 px-6"
            >
              {addingExclude ? "Adding..." : "➕ Add to Exclude List"}
            </Button>
          </div>

          {/* Load existing list */}
          {!excludeData && (
            <Button
              type="button"
              onClick={loadExcludeList}
              disabled={excludeLoading}
              data-testid="load-exclude-btn"
              className="h-10 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold disabled:opacity-60"
            >
              {excludeLoading ? "Loading..." : "📋 Show Excluded Numbers"}
            </Button>
          )}

          {excludeData && (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="px-3 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 text-sm">
                  <span className="text-red-300">Total Excluded: </span>
                  <span className="text-red-200 font-bold">{excludeData.total}</span>
                </div>
                <Button
                  type="button"
                  onClick={loadExcludeList}
                  disabled={excludeLoading}
                  className="h-8 bg-slate-700 hover:bg-slate-600 text-white text-xs px-3 py-1 rounded"
                  data-testid="refresh-exclude-btn"
                >
                  {excludeLoading ? "..." : "🔄 Refresh"}
                </Button>
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await axios.post(
                        `${API}/admin/export-exclude-csv`,
                        { admin_token: token },
                        { responseType: "blob" }
                      );
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement("a");
                      link.href = url;
                      const date = new Date().toISOString().slice(0, 10);
                      link.setAttribute("download", `vee_exclude_list_${date}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success(`📥 ${excludeData.total} numbers downloaded!`);
                    } catch (err) {
                      toast.error("Download failed — check admin token");
                    }
                  }}
                  className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-3 py-1 rounded"
                  data-testid="download-exclude-csv-btn"
                >
                  📥 Download CSV
                </Button>
              </div>

              {excludeData.items.length === 0 ? (
                <p className="text-sm text-slate-500 italic py-4">
                  Abhi koi number/email exclude list mein nahi hai. Upar form se add karo!
                </p>
              ) : (
                <div className="max-h-[420px] overflow-y-auto rounded-lg border border-slate-700">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800/80 sticky top-0 z-10">
                      <tr>
                        <th className="text-left px-3 py-2 text-slate-300 font-semibold">📞 Phone</th>
                        <th className="text-left px-3 py-2 text-slate-300 font-semibold">📧 Email</th>
                        <th className="text-left px-3 py-2 text-slate-300 font-semibold">📝 Note</th>
                        <th className="text-center px-3 py-2 text-slate-300 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excludeData.items.map((it, idx) => {
                        const key = `${it.phone_last10 || ""}|${it.email || ""}`;
                        return (
                          <tr key={key + idx} className="border-t border-slate-800 hover:bg-slate-800/40">
                            <td className="px-3 py-2 text-white">{it.phone || "—"}</td>
                            <td className="px-3 py-2 text-slate-300 text-xs">{it.email || "—"}</td>
                            <td className="px-3 py-2 text-slate-400 text-xs">{it.note || "—"}</td>
                            <td className="px-3 py-2 text-center">
                              <Button
                                type="button"
                                onClick={() => removeFromExcludeList(it.phone || "", it.email || "")}
                                disabled={removingKey === key}
                                data-testid={`remove-exclude-${idx}`}
                                className="h-8 px-3 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                              >
                                {removingKey === key ? "..." : "↺ Remove"}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="text-xs text-purple-400/70 pt-3 font-mono">
                💡 Set once — forget forever! Jab bhi 🔵🟠🔴 button se blast karoge, yeh numbers auto-skip honge.
              </p>
            </div>
          )}
        </div>

        {/* Invoice Generator + Sales Sheet */}
        <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 md:p-8">
          <h2 className="font-display text-xl font-bold text-emerald-400 mb-1 flex items-center gap-2">
            💰 Invoice Generator
          </h2>
          <p className="text-sm text-slate-400 mb-5">
            Course payment ke baad 30 second mein invoice + sales sheet entry banao. PDF auto-WhatsApp + Email se jayega.
          </p>

          {/* Signature & Logo Upload Section */}
          <div className="mb-6 p-4 rounded-lg border border-amber-500/20 bg-amber-500/5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-amber-300">🖼️ Invoice Branding (Upload Once)</h4>
              <div className="text-[10px] text-slate-500">PNG/JPG, transparent bg preferred</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">
                    Signature {sigUploaded && <span className="text-emerald-400">✓ Uploaded</span>}
                  </Label>
                  <Input
                    type="file"
                    accept="image/*"
                    data-testid="upload-signature"
                    onChange={(e) => uploadAsset("signature", e.target.files?.[0])}
                    className="mt-1 h-10 bg-white/5 border-white/10 text-white text-xs file:bg-emerald-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1 file:mr-2"
                  />
                </div>
                {sigUploaded && (
                  <button
                    type="button"
                    onClick={() => deleteAsset("signature")}
                    className="text-xs text-red-400 hover:underline mt-5"
                    data-testid="delete-signature"
                  >
                    ✕ Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">
                    Logo {logoUploaded && <span className="text-emerald-400">✓ Uploaded</span>}
                  </Label>
                  <Input
                    type="file"
                    accept="image/*"
                    data-testid="upload-logo"
                    onChange={(e) => uploadAsset("logo", e.target.files?.[0])}
                    className="mt-1 h-10 bg-white/5 border-white/10 text-white text-xs file:bg-emerald-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1 file:mr-2"
                  />
                </div>
                {logoUploaded && (
                  <button
                    type="button"
                    onClick={() => deleteAsset("logo")}
                    className="text-xs text-red-400 hover:underline mt-5"
                    data-testid="delete-logo"
                  >
                    ✕ Delete
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">
                Invoice No. {invNextNo && <span className="text-emerald-400">(next: {invNextNo})</span>}
              </Label>
              <Input
                data-testid="inv-no"
                value={invCustomNo}
                onChange={(e) => setInvCustomNo(e.target.value)}
                placeholder={`Auto (${invNextNo || "next"}) — or type custom no.`}
                className="mt-1 h-11 bg-white/5 border-white/10 text-white"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Khali chhodo → auto. Ya manual number daalo (e.g. 458).
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Invoice Date</Label>
              <Input
                data-testid="inv-date"
                type="date"
                value={invCustomDate}
                onChange={(e) => setInvCustomDate(e.target.value)}
                className="mt-1 h-11 bg-white/5 border-white/10 text-white"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Khali chhodo → aaj ki date. Ya backdate / future date daalo.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Customer Name *</Label>
              <Input
                data-testid="inv-name"
                autoComplete="off"
                value={invName}
                onChange={(e) => setInvName(e.target.value)}
                onBlur={(e) => setInvName(e.target.value)}
                placeholder="VIPUL ASHOK"
                className="mt-1 h-11 bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Mobile *</Label>
              <Input
                data-testid="inv-mobile"
                autoComplete="off"
                value={invMobile}
                onChange={(e) => setInvMobile(e.target.value)}
                onBlur={(e) => setInvMobile(e.target.value)}
                placeholder="9876543210"
                className="mt-1 h-11 bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">State *</Label>
              <select
                data-testid="inv-state"
                value={invState}
                onChange={(e) => setInvState(e.target.value)}
                className="mt-1 h-11 w-full rounded-md bg-white/5 border border-white/10 text-white px-3"
              >
                <option value="">Select State…</option>
                <option value="Rajasthan">Rajasthan (CGST + SGST)</option>
                <option value="Maharashtra">Maharashtra</option>
                <option value="Gujarat">Gujarat</option>
                <option value="Karnataka">Karnataka</option>
                <option value="Tamil Nadu">Tamil Nadu</option>
                <option value="Delhi">Delhi</option>
                <option value="Uttar Pradesh">Uttar Pradesh</option>
                <option value="West Bengal">West Bengal</option>
                <option value="Telangana">Telangana</option>
                <option value="Andhra Pradesh">Andhra Pradesh</option>
                <option value="Kerala">Kerala</option>
                <option value="Madhya Pradesh">Madhya Pradesh</option>
                <option value="Haryana">Haryana</option>
                <option value="Punjab">Punjab</option>
                <option value="Bihar">Bihar</option>
                <option value="Odisha">Odisha</option>
                <option value="Assam">Assam</option>
                <option value="Jharkhand">Jharkhand</option>
                <option value="Chhattisgarh">Chhattisgarh</option>
                <option value="Uttarakhand">Uttarakhand</option>
                <option value="Himachal Pradesh">Himachal Pradesh</option>
                <option value="Jammu & Kashmir">Jammu & Kashmir</option>
                <option value="Goa">Goa</option>
                <option value="Tripura">Tripura</option>
                <option value="Meghalaya">Meghalaya</option>
                <option value="Manipur">Manipur</option>
                <option value="Nagaland">Nagaland</option>
                <option value="Sikkim">Sikkim</option>
                <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                <option value="Mizoram">Mizoram</option>
                <option value="Chandigarh">Chandigarh</option>
                <option value="Puducherry">Puducherry</option>
                <option value="Andaman & Nicobar">Andaman & Nicobar</option>
                <option value="Dadra & Nagar Haveli">Dadra & Nagar Haveli</option>
                <option value="Daman & Diu">Daman & Diu</option>
                <option value="Lakshadweep">Lakshadweep</option>
                <option value="Ladakh">Ladakh</option>
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                Rajasthan = CGST 9% + SGST 9% (intra-state). Other = IGST 18%.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Email (optional)</Label>
              <Input
                data-testid="inv-email"
                value={invEmail}
                onChange={(e) => setInvEmail(e.target.value)}
                placeholder="customer@gmail.com"
                className="mt-1 h-11 bg-white/5 border-white/10 text-white"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400">GSTIN (optional)</Label>
              <Input
                data-testid="inv-gstin"
                value={invGstin}
                onChange={(e) => setInvGstin(e.target.value)}
                placeholder="22AAAAA0000A1Z5 (only if customer has GST)"
                className="mt-1 h-11 bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Service Type *</Label>
              <select
                data-testid="inv-service-type"
                value={invServiceType}
                onChange={(e) => setInvServiceType(e.target.value)}
                className="mt-1 h-11 w-full rounded-md bg-white/5 border border-white/10 text-white px-3"
              >
                <option value="course">COURSE FEES (HSN 9992)</option>
                <option value="software">SOFTWARE SUBS. (HSN 9973)</option>
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                Course = ₹9,999 default | Software = ₹2,999 default
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Amount (₹) — optional</Label>
              <Input
                data-testid="inv-amount"
                type="number"
                step="0.01"
                value={invAmount}
                onChange={(e) => setInvAmount(e.target.value)}
                placeholder={invServiceType === "software" ? "2999 (default)" : "9999 (default)"}
                className="mt-1 h-11 bg-white/5 border-white/10 text-white"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Partial payment? Yahaan amount daalo (e.g. 5000). Khali → default price.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-4 items-center">
            <label className="inline-flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                data-testid="inv-send-wa"
                checked={invSendWa}
                onChange={(e) => setInvSendWa(e.target.checked)}
                className="h-4 w-4 accent-emerald-500"
              />
              📱 Auto-send WhatsApp (PDF link)
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                data-testid="inv-send-email"
                checked={invSendEmail}
                onChange={(e) => setInvSendEmail(e.target.checked)}
                className="h-4 w-4 accent-emerald-500"
              />
              📧 Auto-send Email (PDF attachment, if email given)
            </label>
          </div>

          <Button
            type="button"
            data-testid="generate-invoice-btn"
            disabled={invGenerating || !invName.trim() || !invMobile.trim() || !invState}
            onClick={async () => {
              setInvGenerating(true);
              try {
                const res = await axios.post(`${API}/admin/invoice/generate`, {
                  admin_token: token,
                  invoice_no: invCustomNo.trim(),
                  invoice_date: invCustomDate,
                  service_type: invServiceType,
                  amount: invAmount.trim() ? Number(invAmount) : null,
                  name: invName.trim(),
                  mobile: invMobile.trim(),
                  state: invState,
                  email: invEmail.trim(),
                  gstin: invGstin.trim(),
                  send_wa: invSendWa,
                  send_email: invSendEmail && !!invEmail.trim(),
                });
                const inv = res.data.invoice;
                toast.success(
                  `✅ Invoice #${inv.invoice_no} generated! ` +
                  (inv.wa_sent ? "WA ✓ " : "") +
                  (inv.email_sent ? "Email ✓" : "")
                );
                // Auto-open PDF in new tab
                window.open(`${API}/invoice/${inv.invoice_no}/pdf`, "_blank");
                // Reset form
                setInvName(""); setInvMobile(""); setInvState("");
                setInvEmail(""); setInvGstin(""); setInvCustomNo(""); setInvCustomDate("");
                setInvAmount(""); setInvServiceType("course");
                // Refresh next-no display + invoices list
                loadNextInvoiceNo();
                if (invoices) await loadInvoices();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Invoice generation failed");
              } finally {
                setInvGenerating(false);
              }
            }}
            className="mt-5 h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-6 rounded-lg disabled:opacity-50"
          >
            {invGenerating ? "Generating…" : "💰 Generate Invoice (₹9,999)"}
          </Button>

          {/* Bulk CSV Upload */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <h3 className="font-bold text-amber-300 text-lg mb-2">📥 Bulk CSV Upload (Generate Many Invoices at Once)</h3>
            <p className="text-xs text-slate-400 mb-3">
              Excel/Google Sheet me customer data maintain karo → CSV export → yahaan upload karo → sab invoices ek saath bann jayegi (PDF + Email + WA sab automatic).
            </p>
            <div className="flex flex-wrap gap-2 mb-3 items-center">
              <a
                href={`${API}/admin/invoice/bulk-csv/template?admin_token=${encodeURIComponent(token)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-bulk-template"
                className="inline-flex items-center gap-1 h-9 bg-slate-700 hover:bg-slate-600 text-white text-xs px-3 rounded"
              >
                📄 Download Sample CSV Template
              </a>
              <label
                data-testid="bulk-csv-input-label"
                className={`inline-flex items-center gap-1 h-9 ${bulkUploading ? "bg-amber-700 cursor-not-allowed" : "bg-amber-600 hover:bg-amber-500 cursor-pointer"} text-white text-xs px-3 rounded`}
              >
                {bulkUploading ? "⏳ Uploading…" : "⬆️ Upload CSV & Generate All"}
                <input
                  type="file"
                  accept=".csv,text/csv"
                  data-testid="bulk-csv-input"
                  disabled={bulkUploading}
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";  // allow re-upload of same file
                    if (!file) return;
                    setBulkUploading(true);
                    setBulkResults(null);
                    try {
                      const csv_text = await file.text();
                      const res = await axios.post(`${API}/admin/invoice/bulk-csv`, {
                        admin_token: token,
                        csv_text,
                      });
                      setBulkResults(res.data);
                      const { success_count, fail_count } = res.data;
                      if (fail_count === 0) {
                        toast.success(`🎉 All ${success_count} invoices generated!`);
                      } else {
                        toast.warning(`⚠️ ${success_count} succeeded, ${fail_count} failed. Check below.`);
                      }
                      loadNextInvoiceNo();
                      if (invoices) await loadInvoices();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Bulk upload failed");
                    } finally {
                      setBulkUploading(false);
                    }
                  }}
                />
              </label>
            </div>
            <div className="text-[10px] text-slate-500 mb-2">
              <b>CSV columns:</b> name, mobile, state, email, gstin, service_type (course/software), amount, invoice_no, invoice_date (DD-MM-YYYY), send_wa (true/false), send_email (true/false)<br/>
              <b>Required:</b> name, mobile, state. Sab dusra optional — khali chhodoge to default value lagega.
            </div>

            {bulkResults && (
              <div className="mt-3 rounded-lg border border-white/10 overflow-hidden">
                <div className="bg-white/5 px-3 py-2 text-xs text-slate-300 flex flex-wrap gap-4">
                  <span>Total: <b>{bulkResults.total_rows}</b></span>
                  <span className="text-emerald-300">✅ Success: <b>{bulkResults.success_count}</b></span>
                  <span className="text-rose-300">❌ Failed: <b>{bulkResults.fail_count}</b></span>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5 sticky top-0">
                      <tr className="text-slate-400 text-left">
                        <th className="p-2">Row</th>
                        <th className="p-2">Name</th>
                        <th className="p-2">Invoice #</th>
                        <th className="p-2 text-right">Amount</th>
                        <th className="p-2 text-center">WA</th>
                        <th className="p-2 text-center">Email</th>
                        <th className="p-2">Status / Error</th>
                        <th className="p-2 text-center">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResults.results.map((r) => (
                        <tr key={r.row} className="border-t border-white/5">
                          <td className="p-2 text-slate-400">{r.row}</td>
                          <td className="p-2 text-white">{r.name}</td>
                          <td className="p-2 font-mono text-slate-200">{r.invoice_no || "—"}</td>
                          <td className="p-2 text-right text-emerald-300">{r.total ? `₹${r.total.toLocaleString("en-IN")}` : "—"}</td>
                          <td className="p-2 text-center">{r.status === "ok" ? (r.wa_sent ? "✓" : "—") : ""}</td>
                          <td className="p-2 text-center">{r.status === "ok" ? (r.email_sent ? "✓" : "—") : ""}</td>
                          <td className={`p-2 ${r.status === "ok" ? "text-emerald-300" : "text-rose-300"}`}>
                            {r.status === "ok" ? "✅ ok" : `❌ ${r.error}`}
                          </td>
                          <td className="p-2 text-center">
                            {r.pdf_url ? (
                              <a
                                href={`${API}${r.pdf_url.replace("/api", "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-400 hover:underline"
                              >
                                📄
                              </a>
                            ) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Sales Sheet */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="font-bold text-emerald-300 text-lg">📊 Sales Sheet</h3>
              <div className="flex gap-2">
                <Button
                  type="button"
                  data-testid="load-invoices-btn"
                  onClick={loadInvoices}
                  disabled={invLoading}
                  className="h-8 bg-slate-700 hover:bg-slate-600 text-white text-xs px-3 rounded"
                >
                  {invLoading ? "Loading…" : "🔄 View All Invoices"}
                </Button>
                <Button
                  type="button"
                  data-testid="download-sales-csv-btn"
                  onClick={async () => {
                    try {
                      const res = await axios.post(
                        `${API}/admin/invoices/export-csv`,
                        { admin_token: token },
                        { responseType: "blob" }
                      );
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement("a");
                      link.href = url;
                      const date = new Date().toISOString().slice(0, 10);
                      link.setAttribute("download", `vee_sales_sheet_${date}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("📥 Sales sheet downloaded!");
                    } catch (err) {
                      toast.error("Download failed");
                    }
                  }}
                  className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-3 rounded"
                >
                  📥 Download CSV (Excel)
                </Button>
                <Button
                  type="button"
                  data-testid="download-all-pdfs-zip-btn"
                  onClick={async () => {
                    if (!window.confirm("Sabhi invoices ke PDFs ek ZIP me download karein?\n(File size ~5-20 MB depending on count)")) return;
                    const toastId = toast.loading("📦 Preparing ZIP... please wait");
                    try {
                      const res = await axios.get(
                        `${API}/admin/invoices/download-all-zip?admin_token=${encodeURIComponent(token)}`,
                        { responseType: "blob" }
                      );
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
                      const link = document.createElement("a");
                      link.href = url;
                      const cd = res.headers["content-disposition"] || "";
                      const nameMatch = cd.match(/filename="([^"]+)"/);
                      const filename = nameMatch ? nameMatch[1] : `Vee_Invoices_ALL_${new Date().toISOString().slice(0, 10)}.zip`;
                      link.setAttribute("download", filename);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("📦 Sab PDFs ZIP me download ho gayi!", { id: toastId });
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "ZIP download failed", { id: toastId });
                    }
                  }}
                  className="h-8 bg-indigo-600 hover:bg-indigo-500 text-white text-xs px-3 rounded font-semibold"
                >
                  📦 Download ALL PDFs (ZIP)
                </Button>
                <Button
                  type="button"
                  data-testid="backfill-amounts-btn"
                  onClick={async () => {
                    if (!window.confirm("Purani invoices ke missing GST/Taxable values recalculate kare?\n(Existing total amount preserve hoga, sirf missing fields fill honge)")) return;
                    try {
                      const res = await axios.post(`${API}/admin/invoices/backfill-amounts`, { admin_token: token });
                      toast.success(`✅ ${res.data.updated} invoices fixed!`);
                      await loadInvoices();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Backfill failed");
                    }
                  }}
                  className="h-8 bg-amber-600 hover:bg-amber-500 text-white text-xs px-3 rounded"
                >
                  🔧 Fix Old Invoices
                </Button>
                <Button
                  type="button"
                  data-testid="delete-all-invoices-btn"
                  onClick={async () => {
                    const confirm1 = window.prompt(
                      "⚠️ SARE invoices delete kar denge — yeh undo NAHI ho sakta!\n\nConfirm karne ke liye type karo: DELETE ALL"
                    );
                    if (confirm1 !== "DELETE ALL") {
                      toast.info("Cancelled — kuch delete nahi hua.");
                      return;
                    }
                    try {
                      const res = await axios.post(`${API}/admin/invoices/delete-all`, {
                        admin_token: token,
                        confirm: true,
                      });
                      toast.success(`🗑️ ${res.data.deleted_invoices} invoices deleted!`);
                      setInvoices(null);
                      loadNextInvoiceNo();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Delete failed");
                    }
                  }}
                  className="h-8 bg-rose-700 hover:bg-rose-600 text-white text-xs px-3 rounded"
                >
                  🗑️ Delete All
                </Button>
              </div>
            </div>

            {invoices && (
              <div>
                <div className="text-xs text-slate-300 mb-3 flex flex-wrap gap-3">
                  <span>Total: <b className="text-emerald-300">{invoices.count}</b> invoices</span>
                  <span>Revenue: <b className="text-emerald-300">₹{(invoices.summary?.total_revenue || 0).toLocaleString("en-IN")}</b></span>
                  <span>Taxable: <b className="text-slate-200">₹{(invoices.summary?.total_taxable || 0).toLocaleString("en-IN")}</b></span>
                  <span>Total GST: <b className="text-amber-300">₹{(invoices.summary?.total_gst || 0).toLocaleString("en-IN")}</b></span>
                </div>
                <div className="overflow-x-auto rounded-lg border border-white/10">
                  <table className="w-full text-xs">
                    <thead className="bg-white/5">
                      <tr className="text-slate-400 text-left">
                        <th className="p-2">#</th>
                        <th className="p-2">Date</th>
                        <th className="p-2">Name</th>
                        <th className="p-2">Mobile</th>
                        <th className="p-2">State</th>
                        <th className="p-2 text-right">Total</th>
                        <th className="p-2 text-center">WA</th>
                        <th className="p-2 text-center">Email</th>
                        <th className="p-2 text-center">PDF</th>
                        <th className="p-2 text-center">🗑️</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.items?.length === 0 && (
                        <tr><td colSpan="10" className="p-3 text-center text-slate-500">No invoices yet</td></tr>
                      )}
                      {invoices.items?.map((inv) => (
                        <tr key={inv.id} className="border-t border-white/5 text-slate-300">
                          <td className="p-2 font-mono text-emerald-300">{inv.invoice_no}</td>
                          <td className="p-2">{fmtDate(inv.date)}</td>
                          <td className="p-2">{inv.name}</td>
                          <td className="p-2">{inv.mobile}</td>
                          <td className="p-2">{inv.state}</td>
                          <td className="p-2 text-right font-mono">₹{Number(inv.total).toLocaleString("en-IN")}</td>
                          <td className="p-2 text-center">{inv.wa_sent ? "✅" : "—"}</td>
                          <td className="p-2 text-center">{inv.email_sent ? "✅" : "—"}</td>
                          <td className="p-2 text-center">
                            <a
                              href={`${API}/invoice/${inv.invoice_no}/pdf`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-emerald-400 hover:underline"
                            >
                              📄
                            </a>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              type="button"
                              data-testid={`delete-invoice-${inv.invoice_no}`}
                              onClick={async () => {
                                if (!window.confirm(`Invoice #${inv.invoice_no} (${inv.name}) delete karu?`)) return;
                                try {
                                  await axios.post(`${API}/admin/invoice/delete`, {
                                    admin_token: token,
                                    invoice_no: inv.invoice_no,
                                  });
                                  toast.success(`Deleted #${inv.invoice_no}`);
                                  await loadInvoices();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || "Delete failed");
                                }
                              }}
                              className="text-rose-400 hover:text-rose-300"
                              title="Delete this invoice"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mt-8 rounded-2xl border border-red-500/30 bg-red-500/5 p-6 md:p-8">
          <h2 className="font-display text-xl font-bold text-red-400 mb-1 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            Danger Zone
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Test data clean karne ke liye. Genuine leads aane se pehle yahan se reset karo.
          </p>
          <Button
            type="button"
            onClick={clearAllRegistrations}
            disabled={clearing}
            data-testid="clear-registrations-btn"
            className="h-11 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold border border-red-500/50 disabled:opacity-60"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {clearing ? "Clearing..." : "Clear All Registrations"}
          </Button>
          <p className="text-xs text-red-400/70 pt-3 font-mono">
            Confirmation type karna hoga &quot;DELETE&quot;. Ye action UNDONE nahi ho sakta.
          </p>
        </div>

        <div className="mt-8 p-5 rounded-xl border border-amber-500/20 bg-amber-500/5">
          <h3 className="text-sm font-bold text-amber-400 mb-2 font-mono uppercase tracking-wider">
            How it works
          </h3>
          <ul className="text-sm text-slate-400 space-y-1.5 list-disc pl-5">
            <li>Date/time + Zoom link save → Live ho jayega instantly.</li>
            <li>Registration confirmation me auto date/time mention hoga.</li>
            <li>Webinar se 1 ghanta pehle → &quot;Send Zoom Link to All&quot; click karo.</li>
            <li>CSV download → Meta Custom Audience / Lookalike ke liye useful.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

// ============ Manual Resend Form Component ============
function ManualResendForm({ token }) {
  const [query, setQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const handleResend = async (channel) => {
    const q = query.trim();
    if (!q) {
      toast.error("Mobile number ya email daalo pehle");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/admin/resend-single`, {
        admin_token: token,
        query: q,
        channel, // "email" | "whatsapp" | "both"
      });
      setLastResult(res.data);
      if (res.data.email_status === "sent" || res.data.wa_status === "sent") {
        toast.success(`✅ ${res.data.name} ko successfully re-sent!`);
      } else {
        toast.warning("⚠️ Send failed — check details below.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter mobile number OR email (e.g. 9876543210 or vipul@gmail.com)"
          data-testid="manual-resend-query"
          className="flex-1 h-11 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
        />
        <button
          type="button"
          disabled={sending}
          onClick={() => handleResend("email")}
          data-testid="resend-email-btn"
          className="h-11 px-4 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-md disabled:opacity-50"
        >
          📧 Resend Email
        </button>
        <button
          type="button"
          disabled={sending}
          onClick={() => handleResend("whatsapp")}
          data-testid="resend-wa-btn"
          className="h-11 px-4 bg-[#25D366] hover:bg-[#20BA5A] text-white text-sm font-semibold rounded-md disabled:opacity-50"
        >
          💬 Resend WhatsApp
        </button>
        <button
          type="button"
          disabled={sending}
          onClick={() => handleResend("both")}
          data-testid="resend-both-btn"
          className="h-11 px-4 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white text-sm font-semibold rounded-md disabled:opacity-50"
        >
          🚀 Send Both
        </button>
      </div>
      {sending && (
        <p className="text-xs text-cyan-300 mt-2">⏳ Sending…</p>
      )}
      {lastResult && (
        <div className="mt-3 p-3 rounded-lg bg-white/5 border border-white/10 text-xs">
          <div className="text-slate-300">
            <b>Lead:</b> {lastResult.name} — {lastResult.mobile} — {lastResult.email || "(no email)"}
          </div>
          {lastResult.email_status && (
            <div className={lastResult.email_status === "sent" ? "text-emerald-400" : "text-rose-400"}>
              📧 Email: {lastResult.email_status}
              {lastResult.email_status !== "sent" && lastResult.email_reason && ` (${lastResult.email_reason})`}
            </div>
          )}
          {lastResult.wa_status && (
            <div className={lastResult.wa_status === "sent" ? "text-emerald-400" : "text-rose-400"}>
              💬 WhatsApp: {lastResult.wa_status}
              {lastResult.wa_status !== "sent" && lastResult.wa_reason && ` (${lastResult.wa_reason})`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



// ============ Blast Frequency Stats Component (3-Webinar Cap) ============
function BlastFrequencyStats({ token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetQuery, setResetQuery] = useState("");

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blast-stats?admin_token=${encodeURIComponent(token)}`);
      setStats(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSingle = async () => {
    if (!resetQuery.trim()) {
      toast.error("Phone ya email daalo");
      return;
    }
    try {
      await axios.post(`${API}/admin/reset-blast-count`, {
        admin_token: token,
        mode: "single",
        query: resetQuery.trim(),
      });
      toast.success(`✅ Counter reset for ${resetQuery}`);
      setResetQuery("");
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const resetAll = async () => {
    if (!window.confirm("SAB users ka counter reset karu?\nSabko fresh 3 webinar chances milenge.")) return;
    try {
      const r = await axios.post(`${API}/admin/reset-blast-count`, {
        admin_token: token,
        mode: "all",
      });
      toast.success(`✅ ${r.data.reset_count} users reset ho gaye`);
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div>
      {!stats && !loading && (
        <button
          onClick={loadStats}
          className="h-9 px-4 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold rounded-md"
          data-testid="load-blast-stats"
        >
          🔄 Load Stats
        </button>
      )}
      {loading && <p className="text-purple-300 text-sm">⏳ Loading…</p>}
      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg bg-white/5 border border-white/10 p-3">
              <div className="text-[10px] uppercase text-slate-400">Total Registered</div>
              <div className="text-2xl font-bold text-white">{stats.total}</div>
            </div>
            <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-3">
              <div className="text-[10px] uppercase text-blue-300">Fresh (0 exposure)</div>
              <div className="text-2xl font-bold text-blue-300">{stats.zero_exposure}</div>
              <div className="text-[10px] text-slate-400">Will receive all msgs</div>
            </div>
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3">
              <div className="text-[10px] uppercase text-emerald-300">Active (1-2 exposure)</div>
              <div className="text-2xl font-bold text-emerald-300">{stats.active}</div>
              <div className="text-[10px] text-slate-400">Still receiving msgs</div>
            </div>
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-3">
              <div className="text-[10px] uppercase text-rose-300">Cold (3+ exposure)</div>
              <div className="text-2xl font-bold text-rose-300">{stats.cold_auto_skipped}</div>
              <div className="text-[10px] text-slate-400">🚫 Auto-skipped</div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
            <input
              type="text"
              value={resetQuery}
              onChange={(e) => setResetQuery(e.target.value)}
              placeholder="Phone ya email → reset counter (VIP override)"
              data-testid="reset-blast-query"
              className="flex-1 h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
            />
            <button
              onClick={resetSingle}
              data-testid="reset-blast-single"
              className="h-10 px-4 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold rounded-md"
            >
              🔄 Reset Single
            </button>
            <button
              onClick={resetAll}
              data-testid="reset-blast-all"
              className="h-10 px-4 bg-rose-700 hover:bg-rose-600 text-white text-sm font-semibold rounded-md"
            >
              🗑️ Reset ALL (fresh chances)
            </button>
            <button
              onClick={loadStats}
              className="h-10 px-3 bg-white/10 hover:bg-white/20 text-white text-sm rounded-md"
            >
              ↻
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ============ Live Trading Blast Response Stats (YES / NOT NOW) ============
function BlastResponseStats({ token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/blast-response-stats?admin_token=${encodeURIComponent(token)}`
      );
      setStats(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };

  const resetStats = async () => {
    if (!window.confirm("Sab YES/NOT NOW responses delete karein? (Naye blast ke pehle karo)")) return;
    try {
      await axios.post(`${API}/admin/blast-response-reset`, { admin_token: token });
      toast.success("✅ Response counters reset");
      setStats(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };

  return (
    <div className="mt-8 rounded-2xl border border-teal-500/30 bg-teal-500/5 p-6 md:p-8">
      <h2 className="font-display text-xl font-bold text-teal-300 mb-1 flex items-center gap-2">
        📊 Live Trading Blast — Response Stats
      </h2>
      <p className="text-sm text-slate-400 mb-4">
        Track kitne users ne <strong className="text-emerald-300">YES, JOIN ME</strong> dabaya vs <strong className="text-rose-300">NOT NOW</strong>. Conversion insight milega.
      </p>

      <div className="flex gap-2 flex-wrap mb-4">
        <button
          onClick={load}
          disabled={loading}
          data-testid="load-blast-response-stats"
          className="h-10 px-4 bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold rounded disabled:opacity-60"
        >
          {loading ? "Loading..." : "🔄 Load Latest Stats"}
        </button>
        {stats && (
          <button
            onClick={resetStats}
            data-testid="reset-blast-responses"
            className="h-10 px-4 bg-rose-600/30 hover:bg-rose-600/50 border border-rose-500/50 text-rose-200 text-sm font-bold rounded"
          >
            🗑️ Reset (before new blast)
          </button>
        )}
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-4 text-center">
              <div className="text-3xl font-black text-white" data-testid="stat-total-sent">
                {stats.total_sent_approx}
              </div>
              <div className="text-xs text-slate-400 mt-1 uppercase tracking-wider">
                Total Leads (potential reach)
              </div>
            </div>
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/40 p-4 text-center">
              <div className="text-3xl font-black text-emerald-300" data-testid="stat-yes-count">
                {stats.yes_count}
              </div>
              <div className="text-xs text-emerald-400/80 mt-1 uppercase tracking-wider">
                ✅ YES, JOIN ME
              </div>
            </div>
            <div className="rounded-xl bg-rose-500/10 border border-rose-500/40 p-4 text-center">
              <div className="text-3xl font-black text-rose-300" data-testid="stat-not-now-count">
                {stats.not_now_count}
              </div>
              <div className="text-xs text-rose-400/80 mt-1 uppercase tracking-wider">
                ❌ NOT NOW
              </div>
            </div>
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/40 p-4 text-center">
              <div className="text-3xl font-black text-amber-300">
                {stats.yes_conversion_pct}%
              </div>
              <div className="text-xs text-amber-400/80 mt-1 uppercase tracking-wider">
                YES conversion
              </div>
            </div>
          </div>

          <div className="text-xs text-slate-400 mb-3 font-mono">
            📈 Response Rate: <strong className="text-teal-300">{stats.response_rate_pct}%</strong>
            &nbsp;·&nbsp; Total Responded: <strong>{stats.total_responded}</strong>
            &nbsp;/&nbsp; {stats.total_sent_approx} sent
          </div>

          {stats.yes_clickers && stats.yes_clickers.length > 0 && (
            <details className="mb-3">
              <summary className="cursor-pointer text-emerald-300 text-sm font-semibold py-2">
                ✅ YES Clickers ({stats.yes_clickers.length}) — click to expand
              </summary>
              <div className="mt-2 max-h-56 overflow-y-auto rounded-lg bg-slate-900/60 border border-slate-700 p-3 text-xs font-mono text-slate-300">
                {stats.yes_clickers.map((c, i) => (
                  <div key={i} data-testid={`yes-clicker-${i}`} className="py-0.5 border-b border-slate-800 last:border-0">
                    {i + 1}. {c.phone} — {c.last_updated?.slice(0, 19).replace("T", " ")}
                  </div>
                ))}
              </div>
            </details>
          )}

          {stats.not_now_clickers && stats.not_now_clickers.length > 0 && (
            <details>
              <summary className="cursor-pointer text-rose-300 text-sm font-semibold py-2">
                ❌ NOT NOW Clickers ({stats.not_now_clickers.length}) — click to expand
              </summary>
              <div className="mt-2 max-h-56 overflow-y-auto rounded-lg bg-slate-900/60 border border-slate-700 p-3 text-xs font-mono text-slate-300">
                {stats.not_now_clickers.map((c, i) => (
                  <div key={i} className="py-0.5 border-b border-slate-800 last:border-0">
                    {i + 1}. {c.phone} — {c.last_updated?.slice(0, 19).replace("T", " ")}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {!stats && !loading && (
        <p className="text-xs text-slate-500 font-mono">
          Click &quot;Load Latest Stats&quot; to see current response counts.
        </p>
      )}
    </div>
  );
}

// ============ Course Portal Manager (Paid Students + Recordings) ============
function CoursePortalManager({ token }) {
  const [tab, setTab] = useState("students");
  const [students, setStudents] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add student form
  const [sName, setSName] = useState("");
  const [sMobile, setSMobile] = useState("");
  const [sEmail, setSEmail] = useState("");
  const [sPayDate, setSPayDate] = useState("");
  const [sLastDate, setSLastDate] = useState("");

  // Add recording form
  const [rTitle, setRTitle] = useState("");
  const [rLink, setRLink] = useState("");
  const [rDate, setRDate] = useState("");

  const loadStudents = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/students/list?admin_token=${encodeURIComponent(token)}`);
      setStudents(r.data.students);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load students");
    } finally { setLoading(false); }
  };

  const loadRecordings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/recordings/list?admin_token=${encodeURIComponent(token)}`);
      setRecordings(r.data.recordings);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load recordings");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === "students") loadStudents();
    else loadRecordings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const addStudent = async () => {
    if (!sName || !sMobile || !sEmail || !sPayDate) {
      toast.error("Name, Mobile, Email, Payment Date zaruri hain"); return;
    }
    try {
      await axios.post(`${API}/admin/students/add`, {
        admin_token: token, name: sName, mobile: sMobile, email: sEmail,
        payment_date: sPayDate, last_access_date: sLastDate,
      });
      toast.success(`✅ ${sName} added`);
      setSName(""); setSMobile(""); setSEmail(""); setSPayDate(""); setSLastDate("");
      loadStudents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    }
  };

  const deleteStudent = async (mobile) => {
    if (!window.confirm(`Delete student ${mobile}?`)) return;
    try {
      await axios.post(`${API}/admin/students/delete`, { admin_token: token, mobile });
      toast.success("Deleted"); loadStudents();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const extendStudent = async (mobile, currentExpiry) => {
    // Quick prompt: enter new expiry date OR number of days to extend
    const input = window.prompt(
      `Current expiry: ${fmtDate(currentExpiry)}\n\n` +
      `Extend access — Enter NEW expiry date (DD-MM-YYYY) OR days to add (e.g. "5" for +5 days):`,
      fmtDate(currentExpiry)
    );
    if (!input) return;
    let newDate = input.trim();
    // If numeric only, treat as days to add to current expiry
    if (/^\d+$/.test(newDate)) {
      const days = parseInt(newDate, 10);
      const base = new Date(currentExpiry);
      if (isNaN(base.getTime())) { toast.error("Current expiry parse error"); return; }
      base.setDate(base.getDate() + days);
      newDate = base.toISOString().slice(0, 10);
    }
    // Accept DD-MM-YYYY or DD/MM/YYYY and convert to YYYY-MM-DD for backend
    const dmy = newDate.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmy) {
      newDate = `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) {
      toast.error("Invalid date. Use DD-MM-YYYY ya sirf number of days daalo.");
      return;
    }
    try {
      await axios.post(`${API}/admin/students/update`, {
        admin_token: token, mobile, last_access_date: newDate,
      });
      toast.success(`✅ Expiry extended to ${fmtDate(newDate)}`);
      loadStudents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const addRecording = async () => {
    if (!rTitle || !rLink) { toast.error("Title aur video link zaruri hain"); return; }
    try {
      await axios.post(`${API}/admin/recordings/add`, {
        admin_token: token, title: rTitle, video_link: rLink, class_date: rDate,
      });
      toast.success(`✅ Recording added`);
      setRTitle(""); setRLink(""); setRDate("");
      loadRecordings();
    } catch (err) { toast.error(err?.response?.data?.detail || "Add failed"); }
  };

  const deleteRecording = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await axios.post(`${API}/admin/recordings/delete`, { admin_token: token, id });
      toast.success("Deleted"); loadRecordings();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const bulkUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    try {
      const csv_text = await file.text();
      const r = await axios.post(`${API}/admin/students/bulk-csv`, { admin_token: token, csv_text });
      const { success, fail, results } = r.data;
      if (fail > 0) {
        // Show detailed errors + copy first 5 to toast
        const errors = (results || []).filter(x => x.status === "error").slice(0, 5);
        const errMsg = errors.map(e => `Row ${e.row} (${e.name || "?"}): ${e.error}`).join(" | ");
        toast.error(`❌ ${fail} rows failed — ${errMsg}${fail > 5 ? ` ...+${fail - 5} more` : ""}`, { duration: 15000 });
        console.error("CSV upload errors:", results);
      }
      if (success > 0) toast.success(`✅ ${success} students added`);
      loadStudents();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
  };

  // Convert ISO YYYY-MM-DD → DD-MM-YYYY (Indian display format) — uses module-level helper


  const downloadStudentsCSV = () => {
    if (!students || students.length === 0) {
      toast.error("Koi paid student list me nahi hai");
      return;
    }
    const headers = ["Name", "Mobile", "Email", "Payment Date", "Access Ends On", "Status"];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = students.map(s => [
      s.name, s.mobile, s.email, s.payment_date, s.last_access_date,
      s.is_active ? "Active" : "Expired"
    ].map(escape).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vee_paid_students_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success(`✅ ${students.length} students CSV downloaded`);
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 border-b border-white/10">
        <button
          onClick={() => setTab("students")}
          className={`px-4 py-2 text-sm font-semibold ${tab==="students"?"text-fuchsia-300 border-b-2 border-fuchsia-400":"text-slate-500"}`}
          data-testid="tab-students"
        >👥 Paid Students ({students.length})</button>
        <button
          onClick={() => setTab("recordings")}
          className={`px-4 py-2 text-sm font-semibold ${tab==="recordings"?"text-fuchsia-300 border-b-2 border-fuchsia-400":"text-slate-500"}`}
          data-testid="tab-recordings"
        >🎬 Recordings ({recordings.length})</button>
      </div>

      {tab === "students" && (
        <div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-3 mb-4">
            <h4 className="text-fuchsia-300 font-bold text-sm mb-3">➕ Add New Paid Student</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              <input value={sName} onChange={(e)=>setSName(e.target.value)} placeholder="Name *" data-testid="student-name-input" className="h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm" />
              <input value={sMobile} onChange={(e)=>setSMobile(e.target.value)} placeholder="Mobile *" data-testid="student-mobile-input" className="h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm" />
              <input value={sEmail} onChange={(e)=>setSEmail(e.target.value)} placeholder="Email *" data-testid="student-email-input" className="h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm" />
              <input type="date" value={sPayDate} onChange={(e)=>setSPayDate(e.target.value)} placeholder="Payment Date *" data-testid="student-paydate-input" className="h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm" />
              <input type="date" value={sLastDate} onChange={(e)=>setSLastDate(e.target.value)} placeholder="Last Access Date (auto +45)" data-testid="student-lastdate-input" className="h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm" />
              <button onClick={addStudent} data-testid="add-student-btn" className="h-10 px-4 bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-semibold rounded-md">+ Add Student</button>
            </div>
            <p className="text-[10px] text-slate-500">Last Access Date khali chhodo → auto Payment Date + 45 days</p>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 h-9 px-3 bg-slate-700 hover:bg-slate-600 cursor-pointer text-xs text-white rounded">
                📥 Bulk CSV Upload
                <input type="file" accept=".csv" className="hidden" onChange={bulkUpload} data-testid="bulk-students-csv" />
              </label>
              <button
                onClick={downloadStudentsCSV}
                data-testid="download-students-csv-btn"
                className="inline-flex items-center gap-2 h-9 px-3 bg-emerald-600 hover:bg-emerald-500 text-xs text-white font-semibold rounded"
              >
                📤 Download Paid Students CSV
              </button>
              <span className="text-[10px] text-slate-500">Columns: name, mobile, email, payment_date, last_access_date</span>
            </div>
          </div>

          <div className="rounded-lg overflow-hidden border border-white/10 max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/5 sticky top-0">
                <tr className="text-slate-400 text-left">
                  <th className="p-2">Name</th><th className="p-2">Mobile</th><th className="p-2">Email</th>
                  <th className="p-2">Paid</th><th className="p-2">Expires</th><th className="p-2">Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="7" className="p-3 text-center text-slate-500">Loading...</td></tr>}
                {!loading && students.length === 0 && <tr><td colSpan="7" className="p-3 text-center text-slate-500">No students yet</td></tr>}
                {students.map((s) => (
                  <tr key={s.mobile} className="border-t border-white/5 text-slate-300">
                    <td className="p-2">{s.name}</td>
                    <td className="p-2 font-mono">{s.mobile}</td>
                    <td className="p-2">{s.email}</td>
                    <td className="p-2">{fmtDate(s.payment_date)}</td>
                    <td className="p-2">{fmtDate(s.last_access_date)}</td>
                    <td className="p-2">{s.is_active ? <span className="text-emerald-400">✅ Active</span> : <span className="text-rose-400">❌ Expired</span>}</td>
                    <td className="p-2 whitespace-nowrap">
                      <button
                        onClick={()=>extendStudent(s.mobile, s.last_access_date)}
                        className="text-amber-300 hover:text-amber-200 mr-2"
                        data-testid={`edit-student-${s.mobile}`}
                        title="Extend / edit expiry"
                      >✏️ Extend</button>
                      <button onClick={()=>deleteStudent(s.mobile)} className="text-rose-400" data-testid={`del-student-${s.mobile}`}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "recordings" && (
        <div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-3 mb-4">
            <h4 className="text-fuchsia-300 font-bold text-sm mb-3">➕ Add New Class Recording</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input value={rTitle} onChange={(e)=>setRTitle(e.target.value)} placeholder="Class Title *" data-testid="recording-title-input" className="h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm md:col-span-1" />
              <input value={rLink} onChange={(e)=>setRLink(e.target.value)} placeholder="Google Drive link *" data-testid="recording-link-input" className="h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm md:col-span-1" />
              <input type="date" value={rDate} onChange={(e)=>setRDate(e.target.value)} data-testid="recording-date-input" className="h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm md:col-span-1" />
              <button onClick={addRecording} data-testid="add-recording-btn" className="h-10 px-4 bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-sm font-semibold rounded-md">+ Add Recording</button>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">💡 Google Drive me: Share → Anyone with link → Viewer + Download OFF ⚙️</p>
          </div>
          <div className="rounded-lg overflow-hidden border border-white/10">
            <table className="w-full text-xs">
              <thead className="bg-white/5">
                <tr className="text-slate-400 text-left">
                  <th className="p-2">Title</th><th className="p-2">Date</th><th className="p-2">Link</th><th></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan="4" className="p-3 text-center text-slate-500">Loading...</td></tr>}
                {!loading && recordings.length === 0 && <tr><td colSpan="4" className="p-3 text-center text-slate-500">No recordings yet</td></tr>}
                {recordings.map((r) => (
                  <tr key={r.id} className="border-t border-white/5 text-slate-300">
                    <td className="p-2">{r.title}</td>
                    <td className="p-2">{r.class_date}</td>
                    <td className="p-2 truncate max-w-[200px]"><a href={r.video_link} target="_blank" rel="noreferrer" className="text-emerald-400 underline">Open ↗</a></td>
                    <td className="p-2"><button onClick={()=>deleteRecording(r.id, r.title)} className="text-rose-400" data-testid={`del-rec-${r.id}`}>🗑️</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

